import { EditEventForm, UpsertEventParams } from '@dispatch/shared/types';
import {
  createDatesOfWeek,
  createDateWithTimeZoneOffset,
  createDayNumberArray,
  getDateFromAddedDays,
} from './DayHelper';
import { v4 as uuidv4 } from 'uuid';
import { endOfDay, startOfDay } from 'date-fns';

export const ResourceEventHelper = (
  values: EditEventForm,
  resourceId: string,
  tenantId: string
): UpsertEventParams[] => {
  const startDate = createDateWithTimeZoneOffset(
    values.isAllDay ? startOfDay(values.startDateTime) : values.startDateTime,
    values.timeZoneId
  );
  const endDate = createDateWithTimeZoneOffset(
    values.isAllDay ? endOfDay(values.endDateTime) : values.endDateTime,
    values.timeZoneId
  );

  if (values.repeat && !values.postalCode) {
    const repeatUntil = getDateFromAddedDays(startDate, +values.repeatFor);

    const datesToAdd: UpsertEventParams[] = [];
    const days = createDatesOfWeek(startDate, endDate);
    const dayNumbers = createDayNumberArray(values.days);

    dayNumbers.forEach((value) => {
      getRepeatedDates({
        repeatedDates: datesToAdd,
        repeatUntil,
        resourceId,
        name: values.name,
        timeZoneId: values.timeZoneId,
        tenantId,
        startDate: days[value]?.startDate,
        endDate: days[value]?.endDate,
        category: values.category,
        isAllDay: values.isAllDay,
      });
    });

    // leaving this here, this is for allowing edit and adding repeated at the same time
    // if (values.eventId) {
    //   datesToAdd[0].externalId = values.eventId;
    // }

    return datesToAdd;
  }

  return [
    createEvent({
      startDate,
      endDate,
      name: values.name,
      resourceId,
      timeZoneId: values.timeZoneId,
      tenantId,
      category: values.category,
      eventId: values.eventId,
      postalCode: values.postalCode,
      isForced: values.isForced,
    }),
  ];
};

const getRepeatedDates = ({
  repeatedDates,
  repeatUntil,
  resourceId,
  name,
  timeZoneId,
  tenantId,
  startDate,
  endDate,
  category,
}: {
  repeatedDates: UpsertEventParams[];
  repeatUntil: Date;
  resourceId: string;
  name: string;
  timeZoneId: string;
  tenantId: string;
  startDate?: Date | null;
  endDate?: Date | null;
  category?: string;
  isAllDay?: boolean;
}): UpsertEventParams[] => {
  if (!startDate || !endDate) {
    return repeatedDates;
  }

  if (startDate <= repeatUntil) {
    repeatedDates.push(
      createEvent({
        startDate,
        endDate,
        name,
        resourceId,
        timeZoneId,
        tenantId,
        category,
      })
    );
    return getRepeatedDates({
      repeatedDates,
      repeatUntil,
      resourceId,
      name,
      timeZoneId,
      tenantId,
      startDate: getDateFromAddedDays(startDate, 7),
      endDate: getDateFromAddedDays(endDate, 7),
      category,
    });
  }

  return repeatedDates.sort(
    (a, b) => Date.parse(a.startDateTime) - Date.parse(b.startDateTime)
  );
};

const createEvent = ({
  startDate,
  endDate,
  name,
  resourceId,
  timeZoneId,
  tenantId,
  category,
  eventId,
  postalCode,
  isForced,
}: {
  startDate: Date;
  endDate: Date;
  name: string;
  resourceId: string;
  timeZoneId: string;
  tenantId: string;
  category?: string;
  eventId?: string;
  postalCode?: string;
  isForced?: boolean;
}): UpsertEventParams => {
  return {
    resourceId: resourceId,
    externalId: eventId || uuidv4(),
    eventName: name,
    tenantId: tenantId,
    source: 'customer', // hard coded
    startDateTime: new Date(startDate).toISOString(),
    endDateTime: new Date(endDate).toISOString(),
    TaskReferenceId: '',
    timeZoneId: timeZoneId,
    category: category,
    locationData: postalCode
      ? {
          locationType: 'PostalCode',
          geoJson: {
            type: 'Feature',
            properties: {
              postalCode: postalCode,
            },
          },
        }
      : undefined,
    isForced: isForced,
  };
};
